import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from 'firebase/app-check';
import { getAI, VertexAIBackend } from 'firebase/ai';
import { LOCAL_PORTS } from '@leanopx/shared';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);

// --- App Check (reCAPTCHA Enterprise) -----------------------------------
// Must run before any Firebase service that App Check protects. We only
// attempt this in PRODUCTION builds (the gate below is exactly
// `import.meta.env.PROD`; emulator runs are DEV-mode so they're
// excluded by construction — no explicit `!usingEmulator` check
// needed). If no reCAPTCHA key is configured we skip gracefully so
// local dev keeps working without any Firebase console setup.
//
// Gate rationale (epic #12): `import.meta.env.PROD` is statically
// replaced at build time — `true` only for `vite build --mode
// production`, `false` for `vite dev` and `--mode test`. Anchoring on
// PROD means the prod bundle keeps the init branch and the minifier
// strips it from every other build. A leaked
// `VITE_USE_FIREBASE_EMULATOR=true` in `.env.local` — which Vite loads
// in *every* mode — can't accidentally bypass App Check because PROD
// builds ignore the emulator switch.
const usingEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';
const appCheckDebug = usingEmulator || import.meta.env.VITE_APP_CHECK_DEBUG === 'true';

// Module-local handle used by `auth-headers.ts` to mint the
// `X-Firebase-AppCheck` header on `/api/*` fetches. Null when the key is
// missing (local dev, unset env) or init throws.
let appCheckInstance: AppCheck | null = null;

export function getAppCheckInstance(): AppCheck | null {
  return appCheckInstance;
}

if (typeof window !== 'undefined' && import.meta.env.PROD) {
  const recaptchaKey = import.meta.env.VITE_APP_CHECK_RECAPTCHA_KEY;
  // Silently skip when no key is provisioned. App Check only engages when
  // the key is present; enabling it in prod is a deployment decision (see
  // the platform docs), not something the console needs to remind us of.
  if (recaptchaKey) {
    try {
      if (appCheckDebug) {
        // The SDK reads this global flag at init time. Must be set BEFORE
        // calling initializeAppCheck.
        (
          self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }
        ).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      }
      appCheckInstance = initializeAppCheck(firebaseApp, {
        provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (err) {
      // Rare: initializeAppCheck throws on malformed providers or a key
      // that doesn't match the origin. Surfacing it via console.warn keeps
      // the error visible without pulling in `logger` (which imports this
      // module and would create a cycle).
      console.warn('[firebase] App Check init failed:', err);
    }
  }
}

export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const storage = getStorage(firebaseApp);

// Firebase AI Logic — VertexAIBackend routes through Vertex AI (project's
// runtime, not the public Gemini Developer API). Stronger no-training
// data-handling defaults — important for the sovereign-intelligence brief.
// Used for latency-sensitive UX (query refinement, tiny summaries); heavier
// synthesis still flows through `/api/ask` and the server-side Vertex
// fallback in apps/functions/src/gemini/.
export const ai = getAI(firebaseApp, { backend: new VertexAIBackend() });

if (usingEmulator) {
  // Defaults flow from `ports.json` via `@leanopx/shared` so a port move
  // in the source of truth doesn't strand a stale literal here. The
  // VITE_*_EMULATOR_PORT env vars (written by `scripts/render-config.mjs`)
  // still win when set — e2e runs use that escape hatch to dodge collisions.
  const authPort = Number(import.meta.env.VITE_AUTH_EMULATOR_PORT ?? LOCAL_PORTS.auth);
  const firestorePort = Number(
    import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ?? LOCAL_PORTS.firestore,
  );
  const storagePort = Number(import.meta.env.VITE_STORAGE_EMULATOR_PORT ?? LOCAL_PORTS.storage);
  connectAuthEmulator(auth, `http://127.0.0.1:${authPort}`, { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', firestorePort);
  connectStorageEmulator(storage, '127.0.0.1', storagePort);
}
